import { mergeDeepRight } from 'ramda';
import { DeepPartial } from '../models/DeepPartial';
import { ManheimLoginQueryParams } from '../models/ManheimLoginQueryParams';
import { DeployEnvironment, ENV, environment } from './ENV';

const DEFAULT_CONFIG = {
  services: {
    mashery: {
      baseUrl: 'https://integration1.api.manheim.com'
    },
    headerFooter: {
      baseUrl: 'https://uhf.manheim.man-qa1.com'
    },
    sdGateway: {
      // Proxied via the dev-server so requests stay on localhost domain (see vite.config)
      baseUrl: '/dev-proxy/api.sellcenter.manheim.man-ba.com',
      ...{
        // Uncomment this to use localhost
        //baseUrl: 'http://localhost:3001'
      }
    },
    sellerDashboardLegacy: {
      // Proxied via the dev-server so requests stay on localhost domain (see vite.config)
      baseUrl: '/dev-proxy/sell.manheim.man-ba.com',
      ...{
        // Uncomment this to use localhost
        //baseUrl: 'http://localhost:3003'
      }
    }
  },
  pubnub: {
    subscribeKey: 'sub-c-0eeff38e-8479-11e4-8319-02ee2ddab7fe',
    publishKey: 'pub-c-7e23dc72-45ab-46df-bbcd-bd1fb4e11879'
  },
  addVehicle: {
    qnaLibraryUrl: 'https://int1-questions-and-announcements-ui.awsgdmownernp.manheim.com/questionsAndAnnouncements.js'
  },
  listingManager: {
    centralizedFacilitationCode: 'QRM1',
    oveBaseUrl: 'https://members.manheim.man-qa1.com'
  },

  shared: {
    routes: {
      mmr: {
        baseUrl: 'https://mmr.manheim.com'
      },
      autocheck: {
        baseUrl: 'https://www.autocheck.com/members/terms.do'
      },
      sellerDashboardLegacy: {
        baseUrl: 'https://sell.manheim.man-ba.com',
        ...{
          // Uncomment this to use localhost
          // baseUrl: 'http://localhost:3003'
        }
      },
      inventory: {
        baseUrl: 'https://inventory.manheim.man-qa1.com'
      },
      webViews: {
        baseUrl: 'https://int1-webviews.awsgdmownernp.manheim.com'
      },
      gfbManagement: {
        baseUrl: 'https://gfbmanagement.manheim.man-ba.com'
      },
      mashery: {
        baseUrl: 'https://integration1.api.manheim.com'
      },
      oauth: {
        logoutBaseUrl: 'https://integration1.api.manheim.com/auth/idp/startSLO.ping',
        loginBaseUrl: 'https://integration1.api.manheim.com/auth/authorization.oauth2',
        defaultLoginParams: {
          /* eslint-disable camelcase */
          adaptor: 'manheim_customer',
          client_id: 'u2nthcjs9audpdb55pnu3u2p',
          response_type: 'code',
          redirect_uri: '__DYNAMICALLY_GENERATED_DURING_RUNTIME__',
          scope: 'openid profile email'
          /* eslint-enable camelcase */
        } as ManheimLoginQueryParams
      }
    }
  },

  // Exceptions are made for the the Jenkins build variables
  build: {
    environment,
    gitSha: ENV.SD_GIT_SHA || 'local',
    buildTimestamp: new Date(ENV.SD_BUILD_TIMESTAMP || 0)
  }
};
type ConfigObject = typeof DEFAULT_CONFIG;
const mergeWithDefault = (partialConfig: DeepPartial<ConfigObject>) =>
  mergeDeepRight(DEFAULT_CONFIG, partialConfig) as ConfigObject;

const configs: Record<DeployEnvironment, ConfigObject> = {
  test: DEFAULT_CONFIG,
  dev: DEFAULT_CONFIG,
  int1: mergeWithDefault({
    services: {
      sdGateway: {
        baseUrl: 'https://api.sellcenter.manheim.man-ba.com'
      },
      sellerDashboardLegacy: {
        baseUrl: 'https://sell.manheim.man-ba.com'
      }
    }
  }),
  int5: mergeWithDefault({
    services: {
      mashery: {
        baseUrl: 'https://integration5.api.manheim.com'
      },
      sdGateway: {
        baseUrl: 'https://api.sellcenter.manheim.man-silo5.com'
      },
      headerFooter: {
        baseUrl: 'https://uhf.manheim.man-silo5.com'
      },
      sellerDashboardLegacy: {
        baseUrl: 'https://sell.manheim.man-silo5.com'
      }
    },
    addVehicle: {
      qnaLibraryUrl:
        'https://int5-questions-and-announcements-ui.awsgdmownernp.manheim.com/questionsAndAnnouncements.js'
    },
    shared: {
      routes: {
        sellerDashboardLegacy: {
          baseUrl: 'https://sell.manheim.man-silo5.com'
        },
        inventory: {
          baseUrl: 'https://inventory.manheim.man-qa1.com'
        },
        webViews: {
          baseUrl: 'https://int5-webviews.awsgdmownernp.manheim.com'
        },
        gfbManagement: {
          baseUrl: 'https://gfbmanagement.manheim.man-sil05.com/'
        },
        mashery: {
          baseUrl: 'https://integration5.api.manheim.com'
        },
        oauth: {
          logoutBaseUrl: 'https://integration5.api.manheim.com/auth/idp/startSLO.ping',
          loginBaseUrl: 'https://integration5.api.manheim.com/auth/authorization.oauth2',
          defaultLoginParams: {
            // eslint-disable-next-line camelcase
            client_id: 'sd4tcbp7ajkdxerb22e7cbs6'
          }
        }
      }
    }
  }),
  uat: mergeWithDefault({
    services: {
      mashery: {
        baseUrl: 'https://uat.api.manheim.com'
      },
      sdGateway: {
        baseUrl: 'https://api.sellcenter.manheim.man-uat.com'
      },
      headerFooter: {
        baseUrl: 'https://uhf.manheim.man-uat.com'
      },
      sellerDashboardLegacy: {
        baseUrl: 'https://sell.manheim.man-uat.com'
      }
    },
    addVehicle: {
      qnaLibraryUrl: 'https://uat-questions-and-announcements-ui.awsgdmowner.manheim.com/questionsAndAnnouncements.js'
    },
    listingManager: {
      centralizedFacilitationCode: 'PRM1',
      oveBaseUrl: 'https://members.manheim.man-uat.com'
    },
    shared: {
      routes: {
        sellerDashboardLegacy: {
          baseUrl: 'https://sell.manheim.man-uat.com'
        },
        inventory: {
          baseUrl: 'https://inventory.manheim.man-uat.com'
        },
        webViews: {
          baseUrl: 'https://uat-webviews.awsgdmowner.manheim.com'
        },
        gfbManagement: {
          baseUrl: 'https://gfbmanagement.manheim.man-uat.com'
        },
        mashery: {
          baseUrl: 'https://uat.api.manheim.com'
        },
        oauth: {
          logoutBaseUrl: 'https://uat.api.manheim.com/auth/idp/startSLO.ping',
          loginBaseUrl: 'https://uat.api.manheim.com/auth/authorization.oauth2',
          defaultLoginParams: {
            // eslint-disable-next-line camelcase
            client_id: 'ezx6gxjte99kc7mme7wm2he9'
          }
        }
      }
    }
  }),
  prod: mergeWithDefault({
    services: {
      mashery: {
        baseUrl: 'https://api.manheim.com'
      },
      sdGateway: {
        baseUrl: 'https://api.sellcenter.manheim.com'
      },
      headerFooter: {
        baseUrl: 'https://uhf.manheim.com'
      },
      sellerDashboardLegacy: {
        baseUrl: 'https://sell.manheim.com'
      }
    },
    addVehicle: {
      qnaLibraryUrl: 'https://prod-questions-and-announcements-ui.awsgdmowner.manheim.com/questionsAndAnnouncements.js'
    },
    listingManager: {
      centralizedFacilitationCode: 'RSAA',
      oveBaseUrl: 'https://members.manheim.com'
    },
    pubnub: {
      subscribeKey: 'sub-c-74052e24-288a-11e4-97e6-02ee2ddab7fe',
      publishKey: 'pub-c-6a6b90c3-6071-4a8a-90d3-070a30d41514'
    },
    shared: {
      routes: {
        sellerDashboardLegacy: {
          baseUrl: 'https://sell.manheim.com'
        },
        inventory: {
          baseUrl: 'https://inventory.manheim.com'
        },
        webViews: {
          baseUrl: 'https://prod-webviews.awsgdmowner.manheim.com'
        },
        gfbManagement: {
          baseUrl: 'https://gfbmanagement.manheim.com/'
        },
        mashery: {
          baseUrl: 'https://api.manheim.com'
        },
        oauth: {
          logoutBaseUrl: 'https://api.manheim.com/auth/idp/startSLO.ping',
          loginBaseUrl: 'https://api.manheim.com/auth/authorization.oauth2',
          defaultLoginParams: {
            // eslint-disable-next-line camelcase
            client_id: 'ku5fggn3wnubbeed37pmdndh'
          }
        }
      }
    }
  })
};

export const APP_CONFIG = configs[environment];
